/* eslint-disable consistent-return */
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";

import { IcoCheck } from "assets/icons";
import { Button, Input } from "components";
import { HeaderWithBack } from "components/HeaderBack";
import { toast } from "react-toastify";
import { changeAppPassword } from "services/auth";
import { store } from "store";
import styles from "./styles.module.scss";

const ChangePasswordSchema = Yup.object().shape({
	old_password: Yup.string().required("Campo obrigatório"),
	new_password: Yup.string()
		.required("Campo obrigatório")
		.min(8, "Campo inválido, verifique as instruções abaixo")
		.matches(/[A-Z]/, "Campo inválido, verifique as instruções abaixo")
		.matches(/[0-9]/, "Campo inválido, verifique as instruções abaixo")
		.matches(/[!@#$%^&*+_=]/, "Campo inválido, verifique as instruções abaixo"),
	new_password_confirm: Yup.string()
		.required("Campo obrigatório")
		.oneOf(
			[Yup.ref("new_password")],
			"A confirmação de senha deve ser igual à senha"
		)
});

interface PasswordInstructionProps {
	isValid: boolean;
	label: string;
}
function PasswordInstruction({
	isValid = false,
	label
}: PasswordInstructionProps) {
	return (
		<div className="flex flex-row items-center">
			<IcoCheck color={isValid ? "#5E219C" : "#868F9C"} className="mr-1" />
			<span
				translate="no"
				className={`text-[12px] font-normal leading-none ${
					isValid ? "text-neutral-900" : "text-[#4B5563]"
				} font-poppins`}
			>
				{label}
			</span>
		</div>
	);
}

type ChangePasswordFormValues = {
	old_password: string;
	new_password: string;
	new_password_confirm: string;
};

export default function ChangePasswordPage() {
	const navigate = useNavigate();
	const registrationInfo = store.getState().registration;

	const [submitting, setSubmitting] = useState<boolean>(false);

	const [changePassword, setChangePassword] =
		useState<ChangePasswordFormValues>({
			old_password: "",
			new_password: "",
			new_password_confirm: ""
		});

	const formik = useFormik({
		initialValues: changePassword,
		validationSchema: ChangePasswordSchema,
		enableReinitialize: true,
		onSubmit: async ({ old_password, new_password, new_password_confirm }) => {
			setSubmitting(true);

			await changeAppPassword(old_password, new_password, new_password_confirm)
				.then(() => {
					setSubmitting(false);

					toast.success("Senha alterada com sucesso!", {
						position: "bottom-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						progress: undefined,
						theme: "light"
					});

					navigate("/perfil");
				})
				.catch((error) => {
					setSubmitting(false);
					let message = "Algo saiu errado. ";

					if (error.code < 500) {
						message += "Verifique os dados digitados e tente novamente.";
						if (error.body.old_password)
							if (error.body.old_password[0] === "Incorrect password")
								formik.errors.old_password = "Senha incorreta";
							else formik.errors.old_password = error.body.old_password;
						if (error.body.new_password)
							formik.errors.new_password = error.body.new_password;
						if (error.body.new_password_confirm)
							formik.errors.new_password_confirm =
								error.body.new_password_confirm;
					} else {
						message += "Entre em contato com o suporte.";
					}

					toast.error(message, {
						position: "bottom-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						progress: undefined,
						theme: "light"
					});
				});
		}
	});

	return (
		<div className={styles.container}>
			<HeaderWithBack label="Alterar senha" backTo={-1} />
			<form onSubmit={formik.handleSubmit} className={styles.form}>
				<div className={styles.container}>
					<Input
						id="old_password"
						name="old_password"
						placeholder="Digite sua senha atual aqui"
						label="Senha atual"
						value={formik.values.old_password || ""}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.old_password && formik.errors.old_password}
						errorMessage={formik.errors.old_password}
						type="password"
					/>
					<Input
						id="new_password"
						name="new_password"
						placeholder="Digite sua senha aqui"
						label="Nova senha"
						type="password"
						value={formik.values.new_password}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.new_password && formik.errors.new_password}
						errorMessage={formik.errors.new_password}
					/>
					<div>
						<PasswordInstruction
							isValid={formik.values.new_password.length >= 8}
							label="A senha deve ter pelo menos 8 caracteres"
						/>
						<PasswordInstruction
							isValid={/[A-Z]/.test(formik.values.new_password)}
							label="1 letra maiúscula"
						/>
						<PasswordInstruction
							isValid={/[0-9]/.test(formik.values.new_password)}
							label="1 número"
						/>
						<PasswordInstruction
							isValid={/.*[!@#$%^&*+_=].*/.test(formik.values.new_password)}
							label="1 caractere especial (! @ # $ % &)"
						/>
					</div>
					<Input
						id="new_password_confirm"
						name="new_password_confirm"
						placeholder="Digite a senha novamente"
						label="Repita a nova senha"
						type="password"
						value={formik.values.new_password_confirm}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.new_password_confirm &&
							formik.errors.new_password_confirm
						}
						errorMessage={formik.errors.new_password_confirm}
					/>
				</div>
				<div className={styles.buttonWrapper}>
					<Button type="submit">Salvar</Button>
				</div>
			</form>
		</div>
	);
}
